import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'sonner';
import { supabase } from '../supabase';
import { useWorkspace } from './WorkspaceContext';
import { generateMockFunnel } from '../utils/mockGenerators';
import { extractJsonFromText } from '../utils/jsonUtils';

export const BusinessBrainContext = createContext();

export const useBusinessBrain = () => {
  const context = useContext(BusinessBrainContext);
  if (!context) {
    throw new Error('useBusinessBrain must be used within a BusinessBrainProvider');
  }
  return context;
};

export const BusinessBrainProvider = ({ children }) => {
  const { activeWorkspace } = useWorkspace();

  const calculateConfidenceScore = (data) => {
      let score = 0;
      const brand = data.brand || {};
      const audience = data.audience || {};

      // Check brand data OR fallback to activeWorkspace
      if (brand.name || activeWorkspace?.name) score += 10;
      if (brand.brandVoice) score += 15;
      if (brand.mission) score += 10;
      if (brand.values) score += 10;
      if (brand.targetAudience) score += 10;

      if (data.products && data.products.length > 0) score += 20;

      if (audience.demographics && (Array.isArray(audience.demographics) ? audience.demographics.length > 0 : !!audience.demographics)) score += 15;
      if (data.strategy?.notes) score += 10;

      return Math.min(100, score);
  };

  const [brainData, setBrainData] = useState({
    brand: {},
    products: [],
    audience: {},
    content: {},
    performance: {},
    strategy: {},
    training: {
      status: 'idle',
      dataPoints: 0,
      lastTraining: 'Never',
      confidenceScore: 0,
      improvementAreas: [],
      analyzedDomains: []
    }
  });
  const [loading, setLoading] = useState(false);
  const [brainId, setBrainId] = useState(null);

  useEffect(() => {
    // Reset state immediately when workspace changes to prevent stale data
    setBrainData({
        brand: {},
        products: [],
        audience: {},
        content: {},
        performance: {},
        strategy: {},
        training: {
      status: 'idle',
      dataPoints: 0,
      lastTraining: 'Never',
      confidenceScore: 0,
      improvementAreas: [],
      analyzedDomains: []
    }
    });
    setBrainId(null);

    let channel = null;

    if (activeWorkspace) {
      fetchBrainData(activeWorkspace.id);

      channel = supabase.channel(`business_brains_changes_${activeWorkspace.id}`)
          .on('postgres_changes', {
              event: '*',
              schema: 'public',
              table: 'business_brains',
              filter: `workspace_id=eq.${activeWorkspace.id}`
          }, () => {
              // Re-fetch data on any change
              fetchBrainData(activeWorkspace.id);
          })
          .subscribe();
    }

    return () => {
        if (channel) {
            supabase.removeChannel(channel);
        }
    };
  }, [activeWorkspace]);

  // Auto-train logic disabled to prevent unwanted regeneration
  /*
  useEffect(() => {
    if (
        !loading &&
        activeWorkspace?.domain &&
        activeWorkspace.domain !== 'example.com' &&
        brainData &&
        brainData.training &&
        brainData.training.lastTraining === 'Never' &&
        Object.keys(brainData.brand || {}).length === 0 &&
        brainData.training.status === 'idle'
    ) {
         let urlToTrain = activeWorkspace.domain;
         if (!urlToTrain.startsWith('http')) {
             urlToTrain = 'https://' + urlToTrain;
         }
         console.log("Auto-training brain from workspace domain:", urlToTrain);
         trainBrain({ type: 'url', url: urlToTrain, workspaceId: activeWorkspace.id });
    }
  }, [activeWorkspace, brainData, loading]);
  */

  const fetchBrainData = async (workspaceId) => {
    try {
      setLoading(true);
      // Use limit(1) to prevent PGRST116 if duplicate rows exist
      // Order by updated_at DESC to pick the latest brain if duplicates exist
      const { data, error } = await supabase
        .from('business_brains')
        .select('*')
        .eq('workspace_id', workspaceId)
        .order('updated_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setBrainId(data.id);
        // Map database columns to state structure if needed
        // The schema uses JSONB columns: brand_identity, products, audience, content_preferences, training_stats
        const parsedData = {
          brand: data.brand_identity || {},
          products: data.products || [],
          audience: data.audience || {},
          content: data.content_preferences || {},
          strategy: data.strategy_data || {},
          workspace_id: data.workspace_id,
          performance: {},
          training: data.training_stats || {
              status: 'idle',
              dataPoints: 0,
              lastTraining: 'Never',
              confidenceScore: 0,
              improvementAreas: [],
              analyzedDomains: []
          }
        };
        // Auto-fix confidence score if 0 but data exists
        if (parsedData.training.confidenceScore === 0) {
             const score = calculateConfidenceScore(parsedData);
             if (score > 0) {
                 parsedData.training.confidenceScore = score;
                 // We don't await this to avoid blocking render, but we trigger an update
                 // This is a side-effect update
                 updateBrainData('training', { ...parsedData.training, confidenceScore: score });
             }
        }

        setBrainData(parsedData);

      } else {
        // Initialize empty brain for new workspace
        setBrainData({
            brand: {},
            products: [],
            audience: {},
            content: {},
            strategy: {},
            workspace_id: workspaceId,
            performance: {},
            training: {
      status: 'idle',
      dataPoints: 0,
      lastTraining: 'Never',
      confidenceScore: 0,
      improvementAreas: [],
      analyzedDomains: []
    }
        });
        setBrainId(null);
      }
    } catch (err) {
      console.error('Error fetching brain data:', err);
    } finally {
      setLoading(false);
    }
  };

  const clearBrainData = async (category = null) => {
    if (!activeWorkspace || !brainId) return;

    if (category) {
        // Clear specific category
        let emptyState;
        if (category === 'products') {
            emptyState = [];
        } else {
            emptyState = {};
        }

        setBrainData(prev => ({
            ...prev,
            [category]: emptyState
        }));

        const updatePayload = {};
        switch(category) {
            case 'brand': updatePayload.brand_identity = {}; break;
            case 'products': updatePayload.products = []; break;
            case 'audience': updatePayload.audience = {}; break;
            case 'content': updatePayload.content_preferences = {}; break;
            case 'strategy': updatePayload.strategy_data = {}; break;
            default: break;
        }

        if (Object.keys(updatePayload).length > 0) {
            const { error } = await supabase.from('business_brains').update(updatePayload).eq('id', brainId);
            if (error) {
                 console.error(error);
                 toast.error(`Failed to clear ${category}`);
            } else {
                 toast.success(`${category === 'content' ? 'Content preferences' : category} cleared`);
            }
        }

    } else {
        // Clear ALL
        const emptyState = {
            brand: {},
            products: [],
            audience: {},
            content: {},
            strategy: {},
            performance: {},
            training: {
      status: 'idle',
      dataPoints: 0,
      lastTraining: 'Never',
      confidenceScore: 0,
      improvementAreas: [],
      analyzedDomains: []
    }
        };

        setBrainData(emptyState);

        const { error } = await supabase.from('business_brains').update({
            brand_identity: {},
            products: [],
            audience: {},
            content_preferences: {},
            strategy_data: {},
            training_stats: emptyState.training
        }).eq('id', brainId);

        if (error) {
                console.error(error);
                toast.error("Failed to reset The Vault");
        } else {
                toast.success("The Vault reset completely");
        }
    }
  };

  const updateBrainData = async (category, newData, targetWorkspaceId = null) => {
    // Determine the target workspace. Default to activeWorkspace.id.
    const workspaceIdToUpdate = targetWorkspaceId || activeWorkspace?.id;
    if (!workspaceIdToUpdate) return;

    const isUpdatingActiveWorkspace = String(workspaceIdToUpdate) === String(activeWorkspace?.id);

    // If updating a background workspace, we might not have its current state in memory.
    // To be safe, we fetch it first so we can merge properly.
    let currentBrainDataForTarget = brainData;
    let targetBrainId = brainId;

    if (!isUpdatingActiveWorkspace) {
        const { data: bgBrain } = await supabase
            .from('business_brains')
            .select('*')
            .eq('workspace_id', workspaceIdToUpdate)
            .order('updated_at', { ascending: false })
            .limit(1)
            .maybeSingle();

        if (bgBrain) {
            targetBrainId = bgBrain.id;
            currentBrainDataForTarget = {
                brand: bgBrain.brand_identity || {},
                products: bgBrain.products || [],
                audience: bgBrain.audience || {},
                content: bgBrain.content_preferences || {},
                strategy: bgBrain.strategy_data || {},
                performance: {},
                training: bgBrain.training_stats || {
              status: 'idle',
              dataPoints: 0,
              lastTraining: 'Never',
              confidenceScore: 0,
              improvementAreas: [],
              analyzedDomains: []
          }
            };
        } else {
            targetBrainId = null;
            currentBrainDataForTarget = {
                brand: {},
                products: [],
                audience: {},
                content: {},
                strategy: {},
                performance: {},
                training: {
      status: 'idle',
      dataPoints: 0,
      lastTraining: 'Never',
      confidenceScore: 0,
      improvementAreas: [],
      analyzedDomains: []
    }
            };
        }
    }

    // Calculate new state first to ensure consistency between Optimistic Update and DB Payload
    let nextStateForCategory;

    // Check if category is array based (products) or object based
    if (category === 'products') {
        nextStateForCategory = newData; // Replacement for array
    } else {
        nextStateForCategory = { ...(currentBrainDataForTarget[category] || {}), ...newData };
    }

    // Prepare new full state for score calculation
    const nextFullState = {
        ...currentBrainDataForTarget,
        [category]: nextStateForCategory
    };

    // Recalculate confidence score if we are changing relevant data
    if (['brand', 'products', 'audience', 'strategy'].includes(category)) {
        const newScore = calculateConfidenceScore(nextFullState);
        if (newScore !== nextFullState.training.confidenceScore) {
            nextFullState.training = {
                ...nextFullState.training,
                confidenceScore: newScore
            };
        }
    }

    // Optimistic update only if we are updating the currently active workspace
    if (isUpdatingActiveWorkspace) {
        setBrainData(nextFullState);
    }

    const updatePayload = {};

    // Map category to DB column
    switch(category) {
        case 'brand': updatePayload.brand_identity = nextStateForCategory; break;
        case 'products': updatePayload.products = nextStateForCategory; break;
        case 'audience': updatePayload.audience = nextStateForCategory; break;
        case 'content': updatePayload.content_preferences = nextStateForCategory; break;
        case 'strategy': updatePayload.strategy_data = nextStateForCategory; break;
        case 'training': updatePayload.training_stats = nextStateForCategory; break;
        default: break;
    }

    // If we updated a data category, also update the confidence score in the same call
    if (['brand', 'products', 'audience', 'strategy'].includes(category)) {
         const newScore = calculateConfidenceScore(nextFullState);
         const currentStats = nextFullState.training || {};
         if (newScore !== currentBrainDataForTarget.training?.confidenceScore) {
             updatePayload.training_stats = {
                 ...currentStats,
                 confidenceScore: newScore
             };
         }
    }

    try {
        if (targetBrainId) {
             const { error } = await supabase.from('business_brains').update(updatePayload).eq('id', targetBrainId);
             if (error) throw error;
        } else {
            // If targetBrainId is null, double-check if one exists for this workspace to avoid duplicates
            const { data: existingBrain } = await supabase
                .from('business_brains')
                .select('id')
                .eq('workspace_id', workspaceIdToUpdate)
                .order('updated_at', { ascending: false })
                .limit(1)
                .maybeSingle();

            if (existingBrain) {
                // Update existing
                if (isUpdatingActiveWorkspace) setBrainId(existingBrain.id);
                const { error } = await supabase.from('business_brains').update(updatePayload).eq('id', existingBrain.id);
                if (error) throw error;
            } else {
                // Create new brain
                const { data, error } = await supabase.from('business_brains').insert([{
                    workspace_id: workspaceIdToUpdate,
                    ...updatePayload
                }]).select().single();

                if (error) throw error;
                if (data && isUpdatingActiveWorkspace) setBrainId(data.id);
            }
        }
    } catch (err) {
        console.error("Error saving brain data", err);
        throw err; // Propagate error
    }
  };

  const handleExtractedData = async (extractedData, passedBrainId = null) => {
      // Smart Merge: Overwrite with new data from analysis
      let hasUpdates = false;
      const updates = {};

      // 1. Brand Identity
      if (extractedData.brand) {
          const currentBrand = brainData.brand || {};
          const newBrand = { ...currentBrand };
          let brandChanged = false;

          // Map extracted fields to internal schema (matching BrandIdentity.jsx)
          if (extractedData.brand.brandName && !currentBrand.name) {
              newBrand.name = extractedData.brand.brandName; // UI uses 'name'
              newBrand.brandName = extractedData.brand.brandName; // Keep legacy/context key
              brandChanged = true;
          }

          if (extractedData.brand.brandVoice && !currentBrand.brandVoice) {
              newBrand.brandVoice = extractedData.brand.brandVoice;
              brandChanged = true;
          }

          if (extractedData.brand.missionStatement && !currentBrand.mission) {
              newBrand.mission = extractedData.brand.missionStatement; // UI uses 'mission'
              newBrand.missionStatement = extractedData.brand.missionStatement;
              brandChanged = true;
          }

          // Handle Values (Array to String for UI compatibility)
          if (extractedData.brand.values && !currentBrand.values) {
              if (Array.isArray(extractedData.brand.values)) {
                  newBrand.values = extractedData.brand.values.join(', ');
              } else {
                  newBrand.values = extractedData.brand.values;
              }
              brandChanged = true;
          }

          if (brandChanged) {
              updates.brand_identity = newBrand;
              setBrainData(prev => ({ ...prev, brand: newBrand }));
              hasUpdates = true;
          }
      }

      // 2. Products
      if (extractedData.products && Array.isArray(extractedData.products)) {
          if (extractedData.products.length > 0) {
              const currentProducts = brainData.products || [];

              // Filter out duplicates (simple name check) and append
              const newProducts = extractedData.products.filter(newP =>
                 !currentProducts.some(existingP => existingP.name?.toLowerCase() === newP.name?.toLowerCase())
              );

              if (newProducts.length > 0) {
                  const updatedProducts = [...currentProducts, ...newProducts];
                  updates.products = updatedProducts;
                  setBrainData(prev => ({ ...prev, products: updatedProducts }));
                  hasUpdates = true;
                  toast.success(`Added ${newProducts.length} new products from analysis`);
              } else {
                  toast.info("No new unique products found (duplicates skipped).");
              }
          } else {
              // Only notify if we explicitly expected products (e.g. context might need to know intent, but here we just warn)
              // Since this function handles general data extraction, we shouldn't error loudly if products weren't the *only* goal,
              // but given the "Import Products" flow triggers this, it's helpful.
              // However, this logic runs for generic URL analysis too.
              // We'll rely on the lack of updates to trigger the generic message below if nothing else matched.
          }
      }

      // 3. Audience
      if (extractedData.audience) {
          const currentAudience = brainData.audience || {};
          const newAudience = { ...currentAudience };
          let audienceChanged = false;

          // Update structured audience data
          if (extractedData.audience.demographics && !currentAudience.demographics) {
              newAudience.demographics = extractedData.audience.demographics;
              audienceChanged = true;
          }
          if (extractedData.audience.painPoints && !currentAudience.painPoints) {
              newAudience.painPoints = extractedData.audience.painPoints;
              audienceChanged = true;
          }

          if (audienceChanged) {
              updates.audience = newAudience;
              setBrainData(prev => ({ ...prev, audience: newAudience }));
              hasUpdates = true;

              // Also map demographics to Brand Identity's 'targetAudience' text field if available
              if (extractedData.audience.demographics) {
                  const demoString = Array.isArray(extractedData.audience.demographics)
                      ? extractedData.audience.demographics.join(', ')
                      : extractedData.audience.demographics;

                  // We need to update brand_identity again
                  // Check if we already have a brand update pending
                  let brandUpdate = updates.brand_identity || { ...brainData.brand };

                  // Only update if it's new/different to avoid overwriting user edits unnecessarily
                  // But for "Import" action, we usually want to overwrite.
                  brandUpdate.targetAudience = brandUpdate.targetAudience || demoString;

                  updates.brand_identity = brandUpdate;
                  setBrainData(prev => ({ ...prev, brand: brandUpdate }));
              }
          }
      }

      // 4. Assets
      if (extractedData.assets && Array.isArray(extractedData.assets)) {
          const assetsToImport = extractedData.assets.slice(0, 7);
          if (assetsToImport.length > 0) {
              toast.info(`Importing ${assetsToImport.length} assets from website...`);
              // Run asynchronously so it doesn't block the rest of the extraction
              (async () => {
                  let importedCount = 0;
                  for (const asset of assetsToImport) {
                      if (!asset.url) continue;
                      try {
                          const { data: importData, error: importError } = await supabase.functions.invoke('import-external-asset', {
                              body: {
                                  url: asset.url,
                                  name: asset.title || 'Imported Asset',
                                  workspaceId: activeWorkspace.id
                              }
                          });

                          if (importError) throw importError;

                          if (importData && importData.publicUrl) {
                              const newAsset = {
                                  workspace_id: activeWorkspace.id,
                                  name: asset.title || 'Website Asset',
                                  type: asset.type === 'video' ? 'video' : 'image',
                                  format: 'custom',
                                  status: 'active',
                                  performance: 'new',
                                  image_url: importData.publicUrl,
                                  metadata: {
                                      source: 'website_scrape',
                                      original_url: asset.url
                                  }
                              };
                              const { error: insertError } = await supabase.from('ad_creatives').insert([newAsset]);
                              if (!insertError) importedCount++;
                          }
                      } catch (err) {
                          console.error("Failed to import asset:", asset.url, err);
                      }
                  }
                  if (importedCount > 0) {
                      toast.success(`Successfully saved ${importedCount} assets to your library.`);
                  }
              })();
          }
      }

      // Save to DB
      if (hasUpdates) {
          const targetId = passedBrainId || brainId;

          if (targetId) {
              const { error } = await supabase.from('business_brains').update(updates).eq('id', targetId);
              if (error) console.error("Error saving extracted intelligence:", error);
              else toast.success("Business Intelligence updated from analysis!");
          } else {
              // Create new record
              const { data, error } = await supabase.from('business_brains').insert([{
                  workspace_id: activeWorkspace.id,
                  ...updates
              }]).select().single();

              if (error) {
                  console.error("Error creating Marketing Intelligence with extracted intelligence:", error);
              } else if (data) {
                  setBrainId(data.id);
                  toast.success("Business Intelligence updated from analysis!");
              }
          }
      } else if (!hasUpdates) {
          // If we had products but they were duplicates, we already toasted.
          // If we had NO data at all (e.g. products empty, brand empty), we toast here.
          // We can check if we found *anything* in extractedData.
          const foundAnything = (extractedData.products?.length > 0) || extractedData.brand || extractedData.audience;

          if (!foundAnything) {
             toast.warning("Analysis complete, but no structured data could be extracted.");
          } else {
             toast.info("Analysis complete. No new data was added to The Vault.");
          }
      }
  };

  const trainBrain = async (newData) => {
    // Update local state to training
    const trainingState = {
        status: 'training',
        dataPoints: (brainData.training.dataPoints || 0) + 1,
        lastTraining: new Date().toISOString(),
        confidenceScore: brainData.training.confidenceScore || 0,
        improvementAreas: brainData.training.improvementAreas || []
    };

    setBrainData(prev => ({
      ...prev,
      training: trainingState
    }));

    try {
        // Call Edge Function
        const { data, error } = await supabase.functions.invoke('train-brain', {
            body: {
                brainId,
                workspaceId: activeWorkspace.id,
                newData
            }
        });

        if (error) {
             // Check for specific error body from Supabase Functions
             let errorMessage = error.message;

             try {
                 // FunctionsHttpError often hides the real error in 'context' which is the Response object
                 if (error.context && typeof error.context.json === 'function') {
                     // Clone response to not disturb other readers if any
                     const errBody = await error.context.clone().json();
                     if (errBody && errBody.error) {
                         errorMessage = errBody.error;
                     }
                 }
             } catch (e) {
                 // Fallback to default message
             }

             if (errorMessage.includes('429') || errorMessage.includes('Rate limit')) {
                  throw new Error("Rate limit exceeded. Please wait before training more documents.");
             }

             // Re-throw with the actual server error message
             throw new Error(errorMessage);
        }

        // Handle Success
        if (data) {
             const finalStats = {
                 ...data.trainingStats,
                 lastTraining: data.trainingStats?.lastTraining || new Date().toISOString()
             };

             if (newData.type === 'url' && newData.url) {
                 try {
                     const domain = new URL(newData.url).hostname;
                     const existingDomains = brainData.training?.analyzedDomains || [];
                     if (!existingDomains.includes(domain)) {
                         finalStats.analyzedDomains = [...existingDomains, domain];
                     } else {
                         finalStats.analyzedDomains = existingDomains;
                     }
                 } catch (e) {
                     console.error("Failed to parse URL domain", e);
                 }
             }

             setBrainData(prev => ({
                ...prev,
                training: finalStats
            }));

            let currentId = brainId;

            // Save stats
            if (currentId) {
                 await supabase.from('business_brains').update({
                    training_stats: finalStats
                }).eq('id', currentId);
            } else {
                 const { data: newBrain } = await supabase.from('business_brains').insert([{
                    workspace_id: activeWorkspace.id,
                    training_stats: finalStats
                }]).select().single();

                if (newBrain) {
                    currentId = newBrain.id;
                    setBrainId(newBrain.id);
                }
            }

            // Handle Extracted Data (for website analysis)
            if (data.extractedData) {
                await handleExtractedData(data.extractedData, currentId);
            }
        }

    } catch (err) {
        console.error("Brain training function failed:", err);
        setBrainData(prev => ({
            ...prev,
            training: { ...prev.training, status: 'failed' }
        }));

        let errorMsg = "Training failed. Please check your API configuration.";

        if (typeof err === 'string') {
            errorMsg = `Training failed: ${err}`;
        } else if (err && typeof err === 'object') {
             if (err.message) {
                 errorMsg = `Training failed: ${err.message}`;
             } else if (err.error && typeof err.error === 'string') {
                 errorMsg = `Training failed: ${err.error}`;
             }
        }

        toast.error(errorMsg);
    }
  };

  const mineReviews = async ({ url, platform, competitors, industry }) => {
    try {
        const { data, error } = await supabase.functions.invoke('mine-reviews', {
            body: {
                url,
                platform,
                competitors,
                industry,
                workspaceId: activeWorkspace.id
            }
        });

        if (error) throw error;
        return data;
    } catch (err) {
        console.error("Review mining failed:", err);
        throw err;
    }
  };

  const saveFunnelLocally = (funnelData) => {
      if (!activeWorkspace) return;
      try {
          const key = `saved_funnels_${activeWorkspace.id}`;
          const stored = localStorage.getItem(key);
          let existing = [];
          if (stored && stored !== 'undefined' && stored !== 'null') {
              existing = JSON.parse(stored);
          }

          // Ensure ID exists
          const funnelToSave = {
              ...funnelData,
              id: funnelData.id || crypto.randomUUID(),
              savedAt: new Date().toISOString()
          };

          // Only keep 1 funnel locally
          const updated = [funnelToSave];
          localStorage.setItem(key, JSON.stringify(updated));
          return funnelToSave.id;
      } catch (e) {
          console.error("Failed to save funnel locally:", e);
          toast.error("Failed to save funnel to local storage.");
      }
  };

  const getSavedFunnels = () => {
      if (!activeWorkspace) return [];
      try {
          const key = `saved_funnels_${activeWorkspace.id}`;
          const stored = localStorage.getItem(key);
          if (!stored || stored === 'undefined' || stored === 'null') return [];
          return JSON.parse(stored);
      } catch (e) {
          return [];
      }
  };

  const generateFunnelCode = async (funnelData, step, targetWorkspaceId = null, variant = 'A', maxRetries = 3) => {
      let attempt = 0;
      const workspaceId = targetWorkspaceId || activeWorkspace?.id;

      // Find the next step in the funnel map to provide proper navigation links
      const outEdge = funnelData?.edges?.find(e => e.from === step.id);
      const nextStepId = outEdge ? outEdge.to : null;

      while (attempt < maxRetries) {
          try {
              // We use standard fetch here to securely support streamed responses from the Edge Function
              // supabase.functions.invoke buffers the entire response and throws 504s on slow generations.
              const { data: session } = await supabase.auth.getSession();
              const { supabaseUrl } = await import('../supabase');
              const response = await fetch(`${supabaseUrl}/functions/v1/generate-funnel-code`, {
                  method: 'POST',
                  headers: {
                      'Authorization': `Bearer ${session?.session?.access_token || import.meta.env.VITE_SUPABASE_ANON_KEY}`,
                      'Content-Type': 'application/json'
                  },
                  body: JSON.stringify({
                      funnelData,
                      step,
                      nextStepId,
                      workspaceId: workspaceId,
                      subdomain: activeWorkspace?.subdomain || 'demo',
                      variant,
                      branding: {
                          name: activeWorkspace?.name || brainData?.brand?.name || '',
                          email: activeWorkspace?.email || brainData?.brand?.email || '',
                          phone: activeWorkspace?.phone || brainData?.brand?.phone || '',
                          domain: activeWorkspace?.domain || brainData?.brand?.website || '',
                          logo: activeWorkspace?.logo || brainData?.brand?.logo || '',
                          brandColors: brainData?.brand?.brandColors || [brainData?.brand?.primaryColor, brainData?.brand?.secondaryColor].filter(Boolean) || [],
                          fonts: brainData?.brand?.fonts || {},
                          visualStyle: brainData?.brand?.visualStyle || '',
                          negativePrompts: brainData?.brand?.negativePrompts || ''
                      }
                  })
              });

              if (!response.ok) {
                  throw new Error(`Failed to generate funnel code: ${response.status} ${response.statusText}`);
              }

              // Read the stream and accumulate it
              const reader = response.body.getReader();
              const decoder = new TextDecoder();
              let aiContent = '';
              let done = false;

              while (!done) {
                  const { value, done: doneReading } = await reader.read();
                  done = doneReading;
                  if (value) {
                      aiContent += decoder.decode(value, { stream: !done });
                  }
              }

              // Clean up markdown wrappers client-side since streaming sends raw content
              const blockMatch = aiContent.match(/```(?:html)?\s*([\s\S]*?)```/i);
              if (blockMatch) {
                  aiContent = blockMatch[1];
              }

              // Extract everything between the first < and last > to remove conversational filler
              const firstTag = aiContent.indexOf('<');
              const lastTag = aiContent.lastIndexOf('>');
              if (firstTag !== -1 && lastTag !== -1 && lastTag > firstTag) {
                  aiContent = aiContent.substring(firstTag, lastTag + 1);
              }

              // The AI often ignores instructions and generates full HTML pages.
              // We must strictly extract ONLY the inner body content so the dynamic wrapper in serve-funnel can inject it.
              const bodyMatch = aiContent.match(/<body[^>]*>([\s\S]*?)<\/body>/i);
              if (bodyMatch) {
                  aiContent = bodyMatch[1];
              } else {
                  // Fallback: forcefully strip full-page boilerplate tags if it generated them without a body wrapper
                  aiContent = aiContent.replace(/<!DOCTYPE[^>]*>/gi, '');
                  aiContent = aiContent.replace(/<\/?html[^>]*>/gi, '');
                  aiContent = aiContent.replace(/<head[^>]*>[\s\S]*?<\/head>/gi, ''); // Strip entirely
                  aiContent = aiContent.replace(/<\/?body[^>]*>/gi, '');
              }

              aiContent = aiContent.trim();

              return { html: aiContent };
          } catch (error) {
              attempt++;
              if (attempt >= maxRetries || (!error.message?.includes('504') && !error.message?.includes('Failed to fetch'))) {
                  console.error("Failed to generate funnel code after retries:", error);
                  throw error;
              }
              await new Promise(resolve => setTimeout(resolve, 2000 * attempt));
          }
      }
  };

  const generateFunnel = async (goal, industry, targetWorkspaceId = null) => {
      const workspaceId = targetWorkspaceId || activeWorkspace?.id;

      // If updating a background workspace, we might not have its current state in memory.
      let currentBrainDataForTarget = brainData;

      if (workspaceId !== activeWorkspace?.id) {
          const { data: bgBrain } = await supabase
              .from('business_brains')
              .select('*')
              .eq('workspace_id', workspaceId)
              .order('updated_at', { ascending: false })
              .limit(1)
              .maybeSingle();

          if (bgBrain) {
              currentBrainDataForTarget = {
                  brand: bgBrain.brand_identity || {},
                  products: bgBrain.products || [],
                  audience: bgBrain.audience || {},
                  content: bgBrain.content_preferences || {},
                  strategy: bgBrain.strategy_data || {},
                  performance: {},
                  training: bgBrain.training_stats || {}
              };
          }
      }

      try {
          const { data: session } = await supabase.auth.getSession();
          const { supabaseUrl } = await import('../supabase');
          const response = await fetch(`${supabaseUrl}/functions/v1/generate-funnel`, {
              method: 'POST',
              headers: {
                  'Authorization': `Bearer ${session?.session?.access_token || import.meta.env.VITE_SUPABASE_ANON_KEY}`,
                  'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                  brainData: currentBrainDataForTarget,
                  goal,
                  industry,
                  workspaceId: workspaceId
              })
          });

          if (!response.ok) {
              const errorText = await response.text();
              throw new Error(`Failed to generate funnel: ${response.status} ${errorText}`);
          }

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let fullContent = '';

          while (true) {
              const { value, done } = await reader.read();
              if (done) break;
              fullContent += decoder.decode(value, { stream: true });
          }

          // Extract JSON from potential markdown wrappers
          const cleanedContent = extractJsonFromText(fullContent);

          // In case the Edge Function successfully streamed a JSON error object
          let data;
          try {
              data = JSON.parse(cleanedContent);
              if (data.error) throw new Error(data.error);
          } catch (e) {
              if (e.message.includes('Server Configuration Error') || data?.error) {
                 throw e;
              }
              // If it fails to parse, it means the AI returned an invalid structure or there was a stream error
              throw new Error(`Invalid response format from AI: ${e.message}`);
          }

          // Auto-save generated funnel
          data.id = data.id || crypto.randomUUID();
          data.savedAt = new Date().toISOString();
          saveFunnelLocally(data);

          return data;
      } catch (err) {
          const errorMsg = typeof err === 'object' && err.message ? err.message : JSON.stringify(err);
          console.warn("Edge function failed, falling back to local generator:", errorMsg);
          const isConfigError = errorMsg.includes('Configuration') || errorMsg.includes('Key');

          if (isConfigError) {
              toast.error(`Server Error: ${errorMsg}. Falling back to simulation.`);
          } else {
              toast.info("Offline Mode: Generating simulation based on your inputs.");
          }
          const mock = generateMockFunnel(goal, industry);
          mock.id = mock.id || crypto.randomUUID();
          mock.savedAt = new Date().toISOString();
          saveFunnelLocally(mock);
          return mock;
      }
  };

  const applyReviewInsights = async (insights) => {
    if (!insights || !activeWorkspace) return;

    let hasUpdates = false;
    const updates = {};
    const currentAudience = brainData.audience || {};
    const currentBrand = brainData.brand || {};

    // 1. Merge Pain Points -> Audience Pain Points
    if (insights.painPoints && insights.painPoints.length > 0) {
        // Simple array merge if it's array, or append if string
        const newPainPoints = Array.isArray(currentAudience.painPoints)
             ? [...new Set([...currentAudience.painPoints, ...insights.painPoints])]
             : (currentAudience.painPoints ? [currentAudience.painPoints, ...insights.painPoints].join(', ') : insights.painPoints.join(', '));

        updates.audience = { ...currentAudience, painPoints: newPainPoints };
        hasUpdates = true;
    }

    // 2. Merge Benefits/Keywords -> Brand Values/Voice context (Optional but useful)
    // We append to 'values' if not too long, or maybe just store in a new field if we had one.
    // For now let's just update values if they are empty, or append if distinct.
    if (insights.benefits && insights.benefits.length > 0) {
         const benefitsStr = insights.benefits.join(', ');
         if (!currentBrand.values) {
             updates.brand_identity = { ...currentBrand, values: benefitsStr };
             hasUpdates = true;
         }
    }

    // 3. Feature Requests -> Strategy Notes (if available)
    if (insights.featureRequests && insights.featureRequests.length > 0) {
        const currentStrategy = brainData.strategy || {};
        const newNotes = (currentStrategy.notes || '') + '\n\nTop Feature Requests from Reviews:\n- ' + insights.featureRequests.join('\n- ');
        updates.strategy_data = { ...currentStrategy, notes: newNotes };
        hasUpdates = true;
    }

    if (hasUpdates) {
        // Optimistic Update
        if (updates.audience) setBrainData(prev => ({ ...prev, audience: updates.audience }));
        if (updates.brand_identity) setBrainData(prev => ({ ...prev, brand: updates.brand_identity }));
        if (updates.strategy_data) setBrainData(prev => ({ ...prev, strategy: updates.strategy_data }));

        const targetId = brainId;
        if (targetId) {
            const { error } = await supabase.from('business_brains').update(updates).eq('id', targetId);
            if (error) {
                console.error("Failed to save review insights to The Vault:", error);
                toast.error("Failed to update The Vault with insights");
            } else {
                toast.success("The Vault updated with review insights!");
            }
        }
    } else {
        toast.info("No new unique insights found to merge.");
    }
  };

  return (
    <BusinessBrainContext.Provider value={{ 
      brainData, 
      updateBrainData, 
      clearBrainData,
      trainBrain,
      mineReviews,
      applyReviewInsights,
      generateFunnel,
      generateFunnelCode,
      getSavedFunnels,
      loading
    }}>
      {children}
    </BusinessBrainContext.Provider>
  );
};
